import { Link, useLocation } from 'react-router-dom'
import { useCommonT } from '../../i18n'
import { useMobileNavSuppressed } from '../../lib/mobileNavSuppression'
import { ARIA_CURRENT_PAGE } from '../../lib/navV2.codes'
import { scanCtaEnabled } from '../../lib/scanCta'
import ScanFab from './ScanFab'
import { BracketTabIcon, BuildTabIcon, CollectionTabIcon, DecksTabIcon, HomeTabIcon } from './navIcons'

// Navigation v2's phone tab bar: Home, Bracket, Build, Collection, Decks
// (Keith 2026-10-17: Build is back in the bar, with v1's wrench). The bar's
// height, its safe-area padding and its ride above the adhesion ad
// (--ad-inset-bottom) are exactly MobileNav v1's. Scan is no longer a tab: it
// is the floating ScanFab, and only with the Scan CTA flag on.

type Tab = { path: string; id: string; Icon: (p: { className?: string }) => React.ReactElement; match: (p: string) => boolean }

const TABS: Tab[] = [
  { path: '/', id: 'home', Icon: HomeTabIcon, match: p => p === '/' },
  { path: '/bracket', id: 'bracket', Icon: BracketTabIcon, match: p => p === '/bracket' },
  { path: '/build', id: 'build', Icon: BuildTabIcon, match: p => p === '/build' },
  { path: '/collection', id: 'collection', Icon: CollectionTabIcon, match: p => p === '/collection' },
  { path: '/decks', id: 'decks', Icon: DecksTabIcon, match: p => p === '/decks' || p.startsWith('/decks/') },
]

export default function MobileNavV2() {
  const { pathname } = useLocation()
  const t = useCommonT()
  // A page with its own pinned phone bar (the saved deck page) takes this
  // slot while that bar is mounted; see src/lib/mobileNavSuppression.ts.
  const suppressed = useMobileNavSuppressed()
  if (suppressed) return null

  const tab = ({ path, id, Icon, match }: Tab) => {
    const active = match(pathname)
    return (
      <Link
        key={path}
        to={path}
        aria-current={active ? ARIA_CURRENT_PAGE : undefined}
        className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1 text-[11px] transition-colors ${
          active ? 'text-purple-300 font-semibold' : 'text-gray-400'
        }`}
      >
        <Icon className="w-6 h-6" />
        <span className="max-w-full truncate">{t(`nav.${id}`)}</span>
      </Link>
    )
  }

  return (
    <>
      <nav
        aria-label={t('nav.tabs_label')}
        // Same box as MobileNav v1: bottom-[var(--ad-inset-bottom)] keeps it
        // above the Mediavine adhesion ad below md (src/lib/adInset.ts), in
        // the className so src/__tests__/overlaysAboveAd.test.ts can see it.
        className="md:hidden fixed bottom-[var(--ad-inset-bottom,0px)] left-0 right-0 z-50 border-t border-gray-800"
        style={{
          backgroundColor: '#111827',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="flex h-14 items-stretch">
          {TABS.map(tab)}
        </div>
      </nav>
      {scanCtaEnabled() && <ScanFab />}
    </>
  )
}
